import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Check, X, Star, MessageSquare, Loader2, Presentation, Download, Home, 
  Camera, Upload, ChevronRight, Grid3X3, Image as ImageIcon, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const ClientReview = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [clientName, setClientName] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadNotes, setUploadNotes] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchBoard = async () => {
      try {
        const response = await axios.get(`${API}/review/${shareToken}`);
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch board:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBoard();
  }, [shareToken]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      uploadClientInspiration(file);
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadClientInspiration(file);
    }
  };

  const uploadClientInspiration = async (file) => {
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        
        await axios.post(`${API}/uploads/boards/${data.board.board_id}/client-inspiration`, {
          image_base64: base64,
          notes: uploadNotes,
          room_name: selectedRoom?.name || 'General'
        });
        
        toast.success('Photo uploaded! Your designer will review it.');
        setShowUploadDialog(false);
        setUploadNotes('');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const enterPresentation = (roomId = null) => {
    const url = roomId 
      ? `/present/${shareToken}?room=${roomId}`
      : `/present/${shareToken}`;
    navigate(url);
  };

  const downloadSummary = async () => {
    try {
      const response = await axios.get(`${API}/review/${shareToken}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data?.board?.project_name || data?.board?.name || 'material-selection'}_summary.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const getItemsByRoom = (environmentId = null) => {
    if (!data?.board?.items) return [];
    return data.board.items.filter(item => 
      environmentId ? item.environment_id === environmentId : !item.environment_id
    );
  };

  const getRoomStats = (items) => {
    const approved = items.filter(i => i.client_status === 'approved').length;
    const rejected = items.filter(i => i.client_status === 'rejected').length;
    const pending = items.filter(i => !i.client_status || i.client_status === 'pending').length;
    return { approved, rejected, pending, total: items.length };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data?.board) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="heading-display text-2xl mb-4">Board Not Found</h1>
          <p className="text-muted-foreground">This link may have expired or been removed.</p>
        </div>
      </div>
    );
  }

  const generalItems = getItemsByRoom(null);
  const generalStats = getRoomStats(generalItems);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <div>
              <h1 className="heading-display text-xl font-medium">{data.board.name}</h1>
              {data.board.project_name && (
                <p className="text-sm text-muted-foreground">{data.board.project_name}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={downloadSummary}>
                <Download className="w-4 h-4 mr-2" /> PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        {/* Client Name Input */}
        {!clientName && (
          <div className="bg-secondary/30 p-6 mb-8 flex items-center gap-4">
            <Label className="whitespace-nowrap">Your Name:</Label>
            <Input
              type="text"
              placeholder="Enter your name to save your feedback"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="max-w-xs"
            />
          </div>
        )}

        {/* Welcome Section */}
        <div className="text-center mb-12">
          <p className="caption-text text-muted-foreground mb-2">Material Selection Review</p>
          <h2 className="heading-display text-3xl md:text-4xl font-medium mb-4">
            Select a Room to Review
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Choose a room below to review the material options. You can approve, reject, or request alternatives.
          </p>
        </div>

        {/* Room Selection Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {/* General Materials */}
          {generalItems.length > 0 && (
            <div 
              className="group bg-white border border-border hover:border-primary transition-colors cursor-pointer overflow-hidden"
              onClick={() => enterPresentation()}
            >
              <div className="aspect-[4/3] bg-secondary relative overflow-hidden">
                {generalItems[0]?.material?.images?.[0] ? (
                  <img 
                    src={generalItems[0].material.images[0]} 
                    alt="General" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Grid3X3 className="w-12 h-12 text-muted-foreground" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <h3 className="heading-display text-xl font-medium">General</h3>
                  <p className="text-sm text-white/80">{generalStats.total} materials</p>
                </div>
              </div>
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-green-600">
                    <Check className="w-4 h-4" /> {generalStats.approved}
                  </span>
                  <span className="flex items-center gap-1 text-red-600">
                    <X className="w-4 h-4" /> {generalStats.rejected}
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="w-4 h-4" /> {generalStats.pending}
                  </span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </div>
          )}

          {/* Room Cards */}
          {data.board.environments?.map(env => {
            const roomItems = getItemsByRoom(env.environment_id);
            const stats = getRoomStats(roomItems);
            
            return (
              <div 
                key={env.environment_id}
                className="group bg-white border border-border hover:border-primary transition-colors cursor-pointer overflow-hidden"
                onClick={() => enterPresentation(env.environment_id)}
              >
                <div className="aspect-[4/3] bg-secondary relative overflow-hidden">
                  {roomItems[0]?.material?.images?.[0] ? (
                    <img 
                      src={roomItems[0].material.images[0]} 
                      alt={env.name} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Home className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <h3 className="heading-display text-xl font-medium">{env.name}</h3>
                    <p className="text-sm text-white/80">{stats.total} materials</p>
                  </div>
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 text-green-600">
                      <Check className="w-4 h-4" /> {stats.approved}
                    </span>
                    <span className="flex items-center gap-1 text-red-600">
                      <X className="w-4 h-4" /> {stats.rejected}
                    </span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Clock className="w-4 h-4" /> {stats.pending}
                    </span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Upload Inspiration Section */}
        <div className="bg-secondary/20 p-8 text-center">
          <div className="max-w-lg mx-auto">
            <Camera className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
            <h3 className="heading-display text-xl font-medium mb-2">Share Your Vision</h3>
            <p className="text-muted-foreground mb-6">
              Upload photos of spaces that inspire you. Your designer will review them and incorporate your ideas.
            </p>
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90">
                  <Upload className="w-4 h-4 mr-2" /> Upload Inspirational Photo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Upload Inspiration</DialogTitle>
                </DialogHeader>
                <div className="mt-4 space-y-4">
                  <div
                    className={`border-2 border-dashed ${dragActive ? 'border-primary bg-primary/5' : 'border-border'} p-8 text-center cursor-pointer transition-colors`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <ImageIcon className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Drop image here or click to browse
                    </p>
                  </div>
                  
                  <div>
                    <Label className="mb-2 block">Notes for your designer (optional)</Label>
                    <Textarea
                      value={uploadNotes}
                      onChange={(e) => setUploadNotes(e.target.value)}
                      placeholder="What do you like about this image? Any specific elements?"
                      className="min-h-[100px]"
                    />
                  </div>
                  
                  {uploading && (
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Overall Summary */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-border p-6 text-center">
            <p className="text-3xl font-bold text-foreground">{data.board.items?.length || 0}</p>
            <p className="text-sm text-muted-foreground">Total Materials</p>
          </div>
          <div className="bg-white border border-border p-6 text-center">
            <p className="text-3xl font-bold text-green-600">
              {data.board.items?.filter(i => i.client_status === 'approved').length || 0}
            </p>
            <p className="text-sm text-muted-foreground">Approved</p>
          </div>
          <div className="bg-white border border-border p-6 text-center">
            <p className="text-3xl font-bold text-red-600">
              {data.board.items?.filter(i => i.client_status === 'rejected').length || 0}
            </p>
            <p className="text-sm text-muted-foreground">Rejected</p>
          </div>
          <div className="bg-white border border-border p-6 text-center">
            <p className="text-3xl font-bold text-muted-foreground">
              {data.board.items?.filter(i => !i.client_status || i.client_status === 'pending').length || 0}
            </p>
            <p className="text-sm text-muted-foreground">Pending</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientReview;
